import AddCircleIcon from '@mui/icons-material/AddCircle';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import {
    Box,
    ButtonBase,
    Checkbox,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { CustomSmallListChip } from '../../../layout/styledcomponents/CustomChip';
import { styled, useTheme } from '@mui/material/styles';
import { gridClasses } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import ProfilePicture from '../Profile/ProfilePicture';
import CustomNoRowsOverlay from '../../shared/NoRowsOverlayCustom';
import { AddPeopleDialog, MoveToTeamDialog } from './TeamMembershipDialogs';

function userProfilePath(row: { firstName?: string; id?: number }) {
    return `/users/${(row.firstName || 'user').replace(/ /g, '').toLowerCase()}/${row.id}`;
}

function memberRoleLabel(
    role: string | undefined,
    viewOnly: boolean | undefined,
    loggedInRole?: string,
) {
    if (role === 'owner_admin' || loggedInRole === 'admin') return 'ADMIN';
    if (role === 'guest_admin') return 'COMPANY ADMIN';
    if (role === 'guest_department_admin') return 'TEAM ADMIN';
    if (viewOnly === true) return 'USER (VIEW ONLY)';
    return 'USER';
}

function memberRoleHighlighted(
    role: string | undefined,
    loggedInRole?: string,
) {
    return Boolean(role?.includes('admin')) || loggedInRole === 'admin';
}

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .MuiTablePagination-toolbar': {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignContent: 'center',
    },
    '& .MuiTablePagination-displayedRows ': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-selectLabel': {
        paddingTop: '14px',
    },
    '& .MuiTablePagination-select': {
        paddingTop: '4px',
    },

    '& .MuiDataGrid-columnHeader': {
        fontSize: '12px',
        fontWeight: 900,
        color: '#000',
        borderBottom: `1px solid ${theme.palette.midGrey.main}`,
    },
    [`& .${gridClasses.row}`]: {
        cursor: 'pointer',
        '&:hover, &.Mui-hovered': {
            backgroundColor: 'none',
            color: `${theme.palette.textAccent.main}`,
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: `${theme.palette.neutral.main}`,
            color: `${theme.palette.textAccent.main}`,
            '&:hover, &.Mui-hovered': {
                backgroundColor: 'none',
                color: `${theme.palette.textAccent.main}`,
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: 'none',
                },
            },
        },
    },
}));

export default function ActiveUserList({ team }: any) {
    const activeUsers = team?.activeUsers;
    const loggedInUser = UserRole();
    const allActiveUsers: any[] = useMemo(
        () => activeUsers?.map((user: any) => user) ?? [],
        [activeUsers],
    );
    const [selection, setSelection] = useState<GridRowSelectionModel>([]);
    const [addOpen, setAddOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [usersToMove, setUsersToMove] = useState<any[]>([]);

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const theme = useTheme();
    const navigate = useNavigate();

    const selectedUsers = allActiveUsers.filter((user) =>
        selection.map(Number).includes(user.id),
    );

    const toggleMember = (id: number) => {
        setSelection((current) =>
            current.map(Number).includes(id)
                ? current.filter((value) => Number(value) !== id)
                : [...current, id],
        );
    };

    const openProfile = (user: { firstName?: string; id?: number }) => {
        navigate(userProfilePath(user), {
            state: team?.id ? { returnTo: `/team/${team.id}/edit` } : undefined,
        });
    };

    const columns: GridColDef[] = [
        {
            field: 'firstName',
            headerName: '',
            type: 'string',
            // width: isMobile ? 261 : isIpad ? 735 : 1327,
            flex: 1,
            minWidth: 180,
            sortable: true,
            renderCell: (params) => {
                const highlighted = memberRoleHighlighted(
                    params?.row.role,
                    loggedInUser?.role,
                );
                return (
                    <List>
                        <ListItem sx={{ padding: '0!important' }}>
                            {isMobile || isIpad ? null : (
                                <ListItemAvatar>
                                    <ProfilePicture
                                        profileDetails={params?.row}
                                        src={params?.row?.profilePicture?.src}
                                        sx={{
                                            width: '40px',
                                            height: '40px',
                                            fontSize: '14px',
                                        }}
                                    />
                                </ListItemAvatar>
                            )}
                            <ListItemText
                                primary={
                                    <Typography>
                                        {params?.value} {params?.row.lastName} -{' '}
                                        <CustomSmallListChip
                                            size='small'
                                            style={{
                                                backgroundColor: highlighted
                                                    ? theme.palette.green.main
                                                    : theme.palette.lightGrey
                                                          .main,
                                                color: highlighted
                                                    ? theme.palette.green
                                                          .contrastText
                                                    : theme.palette.lightGrey
                                                          .contrastText,
                                            }}
                                            label={memberRoleLabel(
                                                params?.row.role,
                                                params?.row?.viewOnly,
                                                loggedInUser?.role,
                                            )}
                                            variant='filled'
                                        />
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {params?.row.email}
                                        {isIpad && params?.row.phone
                                            ? ` · ${params.row.phone}`
                                            : ''}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },

        ...(isIpad
            ? []
            : [
                  {
                      field: 'phone',
                      headerName: 'Phone',
                      type: 'string' as const,
                      width: 180,
                      sortable: true,
                  },
              ]),
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: 1,
                }}
            >
                <Typography
                    component='h2'
                    sx={{ fontSize: '16px', fontWeight: 700 }}
                >
                    Active Users
                </Typography>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    <CustomButton
                        id='moveTeamMembers'
                        variant='outlined'
                        color='primary'
                        fullWidth={isMobile}
                        disabled={selectedUsers.length === 0}
                        onClick={() => {
                            setUsersToMove(selectedUsers);
                            setMoveOpen(true);
                        }}
                    >
                        Move to team
                    </CustomButton>
                    <CustomButton
                        id='addTeamMembers'
                        variant='contained'
                        color='primary'
                        fullWidth={isMobile}
                        startIcon={<AddCircleIcon />}
                        onClick={() => setAddOpen(true)}
                    >
                        Add people
                    </CustomButton>
                </Stack>
            </Stack>
            {isMobile ? (
                <Stack
                    divider={
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />
                    }
                >
                    {allActiveUsers.length === 0 ? (
                        <Typography sx={{ py: 3, color: 'text.secondary' }}>
                            No active users
                        </Typography>
                    ) : (
                        allActiveUsers.map((user) => {
                            const highlighted = memberRoleHighlighted(
                                user.role,
                                loggedInUser?.role,
                            );
                            const name =
                                `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
                            return (
                                <Box
                                    key={user.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 0.5,
                                        py: 1.25,
                                    }}
                                >
                                    <Checkbox
                                        checked={selection
                                            .map(Number)
                                            .includes(user.id)}
                                        onChange={() => toggleMember(user.id)}
                                        inputProps={{
                                            'aria-label': `Select ${name || 'user'}`,
                                        }}
                                        sx={{ p: 0.5 }}
                                    />
                                    <ButtonBase
                                        onClick={() => openProfile(user)}
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: 'block',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {name || 'User'}{' '}
                                            <CustomSmallListChip
                                                size='small'
                                                style={{
                                                    backgroundColor: highlighted
                                                        ? theme.palette.green
                                                              .main
                                                        : theme.palette
                                                              .lightGrey.main,
                                                    color: highlighted
                                                        ? theme.palette.green
                                                              .contrastText
                                                        : theme.palette
                                                              .lightGrey
                                                              .contrastText,
                                                }}
                                                label={memberRoleLabel(
                                                    user.role,
                                                    user.viewOnly,
                                                    loggedInUser?.role,
                                                )}
                                                variant='filled'
                                            />
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {user.email}
                                        </Typography>
                                        {user.phone ? (
                                            <Typography
                                                sx={{ fontSize: '12px' }}
                                            >
                                                {user.phone}
                                            </Typography>
                                        ) : null}
                                    </ButtonBase>
                                </Box>
                            );
                        })
                    )}
                </Stack>
            ) : (
                <Box sx={{ height: 500, width: '100%', minWidth: 0 }}>
                    <StripedDataGrid
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0
                                ? 'even'
                                : 'odd'
                        }
                        sx={{
                            boxShadow: 0,
                            border: 0,
                        }}
                        checkboxSelection
                        disableRowSelectionOnClick
                        onRowClick={(params) => openProfile(params.row)}
                        rowSelectionModel={selection}
                        onRowSelectionModelChange={setSelection}
                        disableColumnFilter
                        disableColumnSelector
                        disableDensitySelector
                        rows={allActiveUsers}
                        columns={columns}
                        slots={{
                            noRowsOverlay: CustomNoRowsOverlay,
                        }}
                    />
                </Box>
            )}
            {team?.id ? (
                <>
                    <AddPeopleDialog
                        key={addOpen ? 'add-open' : 'add-closed'}
                        open={addOpen}
                        onClose={() => setAddOpen(false)}
                        teamId={team.id}
                        teamName={team.name}
                    />
                    <MoveToTeamDialog
                        key={moveOpen ? 'move-open' : 'move-closed'}
                        open={moveOpen}
                        onClose={() => setMoveOpen(false)}
                        onMoved={() => setSelection([])}
                        teamId={team.id}
                        selectedUsers={usersToMove}
                    />
                </>
            ) : null}
        </Box>
    );
}
